import { capture, registerSuperProperties } from './posthog';

/** Modals trackés dans le parcours trial / monétisation */
export type TrialModalId =
  | 'pricing'
  | 'ia_analysis_ready'
  | 'black_friday'
  | 'email_verification_payment';

/** Sources d'ouverture du PricingModal (CTA d'origine) */
export type PricingModalSource =
  | 'trial_banner_desktop'
  | 'trial_banner_sidebar'
  | 'trial_banner_mobile'
  | 'trial_banner_mobile_drawer'
  | 'profile_trial_upgrade'
  | 'ia_analysis_paywall'
  | 'ia_recommendations_paywall'
  | 'session_debrief_paywall'
  | 'session_debrief_inline'
  | 'session_pattern_lesson'
  | 'setups_limit'
  | 'image_upload'
  | 'media_upload'
  | 'atlas_chat'
  | 'atlas_credits'
  | 'journal_atlas_filter'
  | 'account_manager'
  | 'account_manager_limit'
  | 'propfirm_binding'
  | 'tags_upsell'
  | 'plans_upsell'
  | 'basic_trade_limit'
  | 'backtest_session_limit'
  | 'backtest_trade_limit'
  | 'basic_sidebar'
  | 'basic_mobile_drawer'
  | 'trade_details_chart'
  | 'pricing_page'
  | 'unknown';

/** Variantes de copy du CTA upgrade Basic (feature flag `basic-upgrade-cta`) */
export type BasicUpgradeCtaVariant =
  /** « Voir mes erreurs » — bénéfice, c'est la fonctionnalité n°1 absente de Basic */
  | 'default'
  /** « Revoir mes {n} trades » — personnalisée, servie dès que la limite Basic masque des trades */
  | 'archived_trades'
  /** « Débloquer mon analyse » — cadrage paywall (variante flag) */
  | 'unlock_framing'
  /** « Passer Premium » — contrôle générique du marché (variante flag) */
  | 'generic_upgrade';

export type TrialFunnelId =
  | 'signup'
  | 'email_verification'
  | 'onboarding'
  | 'demarrage'
  | 'trial_activation'
  | 'trial_monetization'
  | 'basic_upsell';

interface BaseProps {
  is_trial?: boolean;
  is_home_promo?: boolean;
}

/** Clic sur un CTA trial / paywall (avant ouverture modal) */
export function trackTrialCtaClicked(props: {
  source: PricingModalSource | string;
  feature?: string;
  cta_label?: string;
  opens_modal?: TrialModalId | 'pricing' | 'none' | 'page';
  redirect_to?: string;
} & BaseProps) {
  capture('trial_cta_clicked', props);
}

interface BasicUpgradeCtaProps {
  /** 'sidebar' (desktop) ou 'mobile_drawer' */
  position: 'sidebar' | 'mobile_drawer';
  /** Variante de copy servie (feature flag `basic-upgrade-cta`) */
  variant: BasicUpgradeCtaVariant;
  /** Libellé réellement affiché (utile quand la copy évolue) */
  cta_label: string;
  /** Trades archivés par la limite Basic au moment de l'affichage (0 = sous la limite) */
  archived_trades?: number;
  /** Trades stockés sur le compte sélectionné */
  stored_trades?: number;
}

/**
 * Impression du CTA upgrade Basic (sidebar / drawer mobile).
 * Émis une seule fois par session et par position — c'est le dénominateur
 * du funnel Basic → Premium.
 */
export function trackBasicUpgradeCtaViewed(props: BasicUpgradeCtaProps) {
  capture('basic_upgrade_cta_viewed', props);
}

/**
 * Clic sur le CTA upgrade Basic. Émet l'event dédié `basic_upgrade_cta_clicked`
 * (funnel Basic) ET le `trial_cta_clicked` historique (dashboards existants).
 */
export function trackBasicUpgradeCtaClicked(props: BasicUpgradeCtaProps & {
  source: PricingModalSource;
}) {
  const { source, ...rest } = props;
  capture('basic_upgrade_cta_clicked', { source, ...rest });
  trackTrialCtaClicked({
    source,
    feature: 'basic_upgrade_cta',
    cta_label: rest.cta_label,
    opens_modal: 'pricing',
    is_trial: false,
  });
}

/** Modal ouvert */
export function trackTrialModalOpened(props: {
  modal: TrialModalId;
  source: string;
  custom_title?: string;
  trial_stage?: string;
  billing_interval?: string;
} & BaseProps) {
  // Colle la source aux events suivants (checkout, retour Stripe, person) pour
  // relier « a ouvert le modal depuis X » et « a payé ».
  registerSuperProperties({ pricing_modal_source: props.source });
  capture('trial_modal_opened', props);
  // Rétrocompat dashboards existants
  if (props.modal === 'pricing') {
    capture('pricing_modal_opened', {
      source: props.source,
      is_trial: props.is_trial,
      billing_interval: props.billing_interval,
      trial_stage: props.trial_stage,
    });
  }
}

/** Modal fermé sans conversion */
export function trackTrialModalClosed(props: {
  modal: TrialModalId;
  source: string;
  billing_interval?: string;
} & BaseProps) {
  capture('trial_modal_closed', props);
}

/** Redirection vers Stripe checkout */
export function trackStripeCheckoutLaunched(props: {
  source: string;
  modal?: TrialModalId | 'none';
  plan: string;
  billing_interval: 'monthly' | 'yearly';
  is_trial?: boolean;
  is_home_promo?: boolean;
}) {
  registerSuperProperties({ pricing_modal_source: props.source });
  capture('stripe_checkout_launched', props);
  capture('checkout_started', {
    source: props.source,
    modal: props.modal ?? 'none',
    plan: props.plan,
    billing_interval: props.billing_interval,
    is_trial: props.is_trial,
  });
}

/** Changement mensuel/annuel dans le modal pricing */
export function trackPricingIntervalChanged(props: {
  source: string;
  billing_interval: 'monthly' | 'yearly';
  modal: TrialModalId;
}) {
  capture('pricing_interval_changed', props);
}

/** Paywall affiché / cliqué (legacy + enrichi) */
export function trackPaywallHit(props: {
  feature: string;
  action: 'view' | 'click';
  source?: string;
  opens_modal?: TrialModalId | 'page';
}) {
  capture('paywall_hit', props);
  if (props.action === 'click') {
    trackTrialCtaClicked({
      source: props.source ?? props.feature,
      feature: props.feature,
      opens_modal: props.opens_modal ?? 'pricing',
    });
  }
}

/** Étapes funnel inscription / onboarding / activation */
export function trackFunnelStep(props: {
  funnel: TrialFunnelId;
  step: string;
  status: 'viewed' | 'started' | 'completed' | 'failed' | 'skipped';
  error?: string;
  metadata?: Record<string, unknown>;
} & BaseProps) {
  const { funnel, step, status, error, metadata, is_trial, is_home_promo } = props;

  capture('funnel_step', {
    funnel,
    step,
    status,
    ...(error ? { error } : {}),
    ...(metadata ?? {}),
    ...(typeof is_trial === 'boolean' ? { is_trial } : {}),
    ...(typeof is_home_promo === 'boolean' ? { is_home_promo } : {}),
  });
}

/** Ouvre le PricingModal avec tracking CTA */
export function openPricingModalWithTracking(
  open: () => void,
  props: {
    source: PricingModalSource | string;
    feature?: string;
    cta_label?: string;
  },
) {
  trackTrialCtaClicked({
    source: props.source,
    feature: props.feature,
    cta_label: props.cta_label,
    opens_modal: 'pricing',
  });
  capture('paywall_hit', {
    feature: props.feature ?? props.source,
    action: 'click',
    source: props.source,
    opens_modal: 'pricing',
  });
  open();
}

/** Mur démo trial */
export function trackDemoWallShown(props: { pages_visited: number; path: string }) {
  capture('onboarding_demo_wall_shown', props);
}
